import { parseCloudType, parseCloudLogins } from './license';

const CLIENT_LIST_CACHE_KEY = 'client_list_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 60 minutes in milliseconds

class ClientListCache {
  constructor() {
    this.cache = this.loadFromStorage();
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(CLIENT_LIST_CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if cache is expired
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          return parsed.data;
        } else {
          // Cache expired, clear it
          localStorage.removeItem(CLIENT_LIST_CACHE_KEY);
        }
      }
      return { records: [], total: 0, filters: {} };
    } catch (error) {
      console.error('Error loading client list cache:', error);
      // If we hit a quota exceeded error, clear the cache and try again
      if (error.name === 'QuotaExceededError') {
        localStorage.removeItem(CLIENT_LIST_CACHE_KEY);
      }
      return { records: [], total: 0, filters: {} };
    }
  }

  saveToStorage() {
    const cacheData = {
      data: this.cache,
      timestamp: Date.now()
    };
    try {
      localStorage.setItem(CLIENT_LIST_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving client list cache:', error);
      // If we hit quota exceeded, clear old cache and try once more
      if (error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing cache');
        localStorage.removeItem(CLIENT_LIST_CACHE_KEY);
        try {
          localStorage.setItem(CLIENT_LIST_CACHE_KEY, JSON.stringify(cacheData));
        } catch (retryError) {
          console.error('Still failed after clearing cache:', retryError);
        }
      }
    }
  }

  get(filters = {}) {
    // Check if filters match cached filters
    if (this.filtersMatch(filters, this.cache.filters)) {
      return {
        records: this.cache.records || [],
        total: this.cache.total || 0,
        fromCache: true
      };
    }
    return { records: [], total: 0, fromCache: false };
  }

  set(records, total, filters = {}) {
    // Store only essential fields to reduce storage size
    const compactRecords = records.map(record => ({
      uuid: record.uuid,
      name: record.name,
      code: record.code,
      enabled: record.enabled,
      state: record.permissions?.state,
      billtype: record.permissions?.billtype,
      desktop_version: record.desktop_version,
      license_date: record.permissions?.license_date,
      cloud_type: parseCloudType(record.permissions?.celicense),
      cloud_users: parseCloudLogins(record.permissions?.celicense),
      created_at: record.created_at,
      updated_at: record.updated_at
    }));

    this.cache = {
      records: compactRecords,
      total: total,
      filters: filters
    };
    this.saveToStorage();
  }

  // Append more records to the cache (for pagination)
  append(newRecords, total, filters = {}) {
    // Only append if filters match
    if (this.filtersMatch(filters, this.cache.filters)) {
      const existingUuids = new Set(this.cache.records.map(r => r.uuid));
      const uniqueNewRecords = newRecords.filter(r => !existingUuids.has(r.uuid));

      // Store only essential fields to reduce storage size
      const compactNewRecords = uniqueNewRecords.map(record => ({
        uuid: record.uuid,
        name: record.name,
        code: record.code,
        enabled: record.enabled,
        state: record.permissions?.state,
        billtype: record.permissions?.billtype,
        desktop_version: record.desktop_version,
        license_date: record.permissions?.license_date,
        cloud_type: parseCloudType(record.permissions?.celicense),
        cloud_users: parseCloudLogins(record.permissions?.celicense),
        created_at: record.created_at,
        updated_at: record.updated_at
      }));

      this.cache = {
        records: [...this.cache.records, ...compactNewRecords],
        total: total,
        filters: filters
      };
      this.saveToStorage();
      return true;
    }
    return false;
  }

  // Update a single client record in the cache
  updateClient(uuid, clientData) {
    if (this.cache.records && Array.isArray(this.cache.records)) {
      const index = this.cache.records.findIndex(record => record.uuid === uuid);
      if (index !== -1) {
        // Update the record
        this.cache.records[index] = { ...this.cache.records[index], ...clientData };
        this.saveToStorage();
        return true;
      }
    }
    return false;
  }

  filtersMatch(filters1, filters2) {
    const keys1 = Object.keys(filters1).sort();
    const keys2 = Object.keys(filters2).sort();

    if (keys1.length !== keys2.length) {
      return false;
    }

    return keys1.every(key => filters1[key] === filters2[key]);
  }

  clear() {
    this.cache = { records: [], total: 0, filters: {} };
    localStorage.removeItem(CLIENT_LIST_CACHE_KEY);
  }

  isExpired() {
    try {
      const stored = localStorage.getItem(CLIENT_LIST_CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Date.now() - parsed.timestamp >= CACHE_DURATION;
      }
      return true;
    } catch (error) {
      return true;
    }
  }

  getCacheStats() {
    const count = this.cache.records ? this.cache.records.length : 0;
    const stored = localStorage.getItem(CLIENT_LIST_CACHE_KEY);
    let age = 0;
    let sizeInBytes = 0;
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        age = Date.now() - parsed.timestamp;
        sizeInBytes = new Blob([stored]).size;
      } catch (error) {
        // ignore
      }
    }
    return {
      count,
      age,
      expired: this.isExpired(),
      sizeInBytes,
      sizeInMB: (sizeInBytes / 1024 / 1024).toFixed(2)
    };
  }
}

// Create singleton instance
const clientListCache = new ClientListCache();

export default clientListCache;
